import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import { useState, useRef } from 'react'
import { VideoUploadParams } from '../../types'

interface VideoUploadModalProps {
  onClose: () => void
  onUpload: (file: File | null, params: VideoUploadParams) => void
  uploading?: boolean
  title?: string
  hideFileInput?: boolean
  defaultParams?: VideoUploadParams
}

export default function VideoUploadModal({
  onClose,
  onUpload,
  uploading,
  title = 'Upload Video',
  hideFileInput = false,
  defaultParams,
}: VideoUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [params, setParams] = useState<VideoUploadParams>(
    defaultParams || {
      extract_frames: true,
      extraction_method: 'uniform',
      num_frames: 25,
      start_frame: 0,
      end_frame: -1,
    }
  )

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const validTypes = ['video/mp4', 'video/avi', 'video/quicktime', 'video/x-matroska']
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid video file (MP4, AVI, MOV, MKV)')
        return
      }
      setSelectedFile(file)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!hideFileInput && !selectedFile) {
      alert('Please select a video file')
      return
    }
    onUpload(selectedFile, params)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* File Upload Area */}
          {!hideFileInput && (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <CloudArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              {selectedFile ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Drop your video here, or click to browse
                  </p>
                  <p className="text-xs text-gray-500">Supported formats: MP4, AVI, MOV, MKV</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Extraction Settings */}
          <div className="space-y-4 mb-6">
            {(hideFileInput || !hideFileInput) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Extraction Method
                  </label>
                  <select
                    value={params.extraction_method}
                    onChange={e =>
                      setParams({
                        ...params,
                        extraction_method: e.target.value as 'uniform' | 'clustering',
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled
                  >
                    <option value="uniform">Uniform (evenly spaced)</option>
                    <option value="clustering">Smart Selection (AI-based)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Frames
                  </label>
                  <input
                    type="number"
                    value={params.num_frames}
                    onChange={e =>
                      setParams({ ...params, num_frames: parseInt(e.target.value) || 25 })
                    }
                    min="1"
                    max="1000"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Number of frames to extract from the video
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Frame (optional)
                    </label>
                    <input
                      type="number"
                      value={params.start_frame}
                      onChange={e =>
                        setParams({ ...params, start_frame: parseInt(e.target.value) || 0 })
                      }
                      min="0"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Frame (optional)
                    </label>
                    <input
                      type="number"
                      value={params.end_frame}
                      onChange={e =>
                        setParams({ ...params, end_frame: parseInt(e.target.value) || -1 })
                      }
                      min="-1"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use -1 for last frame</p>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={uploading}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center"
              disabled={(!hideFileInput && !selectedFile) || uploading}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                  {hideFileInput ? 'Extract Frames' : 'Upload Video'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
